import React, { useMemo } from "react";
import { Grid, Layout, Menu } from "antd";
import { ThunderboltFilled } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";

import { DASHBOARD } from "../../constants/routes";
import { LOGO_URL, BRAND_NAME } from "../../constants/brand";
import { SECTION_GROUPS } from "../../constants/dashboardSections";
import { useAllowedSections, useHighlightedSection } from "./useDashboardSections";

const { Sider } = Layout;
const { useBreakpoint } = Grid;

const Sidebar = ({ collapsed, onCollapse }) => {
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const allowedSections = useAllowedSections();
  const activeSection = useHighlightedSection();

  // Phones: sidebar is hidden and slides over the content when opened
  const isMobile = screens.md === false;

  /* 🔹 Only sections the user is allowed to see, grouped. Menu keys are the
        section routes, so the sidebar follows the URL. */
  const groups = useMemo(
    () =>
      SECTION_GROUPS.map((group) => ({
        ...group,
        children: allowedSections
          .filter((section) => section.group === group.key)
          .map((section) => ({
            key: section.path,
            icon: section.icon,
            label: section.label,
          })),
      })).filter((group) => group.children.length),
    [allowedSections]
  );

  const menuItems = useMemo(() => {
    if (collapsed) {
      // Group titles don't fit when collapsed, use dividers instead
      return groups.flatMap((group, index) => [
        ...(index ? [{ type: "divider", key: `divider-${group.key}` }] : []),
        ...group.children,
      ]);
    }

    return groups.map((group) => ({
      type: "group",
      key: group.key,
      label: group.label,
      children: group.children,
    }));
  }, [groups, collapsed]);

  const closeOnMobile = () => {
    if (isMobile) {
      onCollapse(true);
    }
  };

  const onMenuClick = ({ key }) => {
    navigate(key);
    closeOnMobile();
  };

  const onBrandClick = () => {
    navigate(DASHBOARD);
    closeOnMobile();
  };

  return (
    <>
      <Sider
        theme="dark"
        width={248}
        collapsedWidth={isMobile ? 0 : 72}
        collapsible
        collapsed={collapsed}
        onCollapse={onCollapse}
        breakpoint="lg"
        trigger={null}
        className={`app-sider${isMobile ? " app-sider--overlay" : ""}`}
      >
        <div
          className={`app-sider__brand${
            collapsed ? " app-sider__brand--collapsed" : ""
          }`}
          onClick={onBrandClick}
        >
          {collapsed ? (
            <span className="app-sider__brand-mark">
              <ThunderboltFilled />
            </span>
          ) : (
            <>
              <span className="app-sider__logo">
                <img src={LOGO_URL} alt="GoGo Power" draggable={false} />
              </span>
              <span className="app-sider__brand-text">{BRAND_NAME}</span>
            </>
          )}
        </div>

        <Menu
          theme="dark"
          mode="inline"
          className="app-sider__menu"
          selectedKeys={activeSection ? [activeSection.path] : []}
          items={menuItems}
          onClick={onMenuClick}
        />
      </Sider>

      {isMobile && !collapsed && (
        <div className="app-sider-backdrop" onClick={() => onCollapse(true)} />
      )}
    </>
  );
};

export default Sidebar;
